import { useEffect, useRef, useCallback } from "react";

interface KeyDef {
  note: string;
  key: string;
  isBlack: boolean;
}

const KEYS: KeyDef[] = [
  { note: "C", key: "a", isBlack: false },
  { note: "C#", key: "w", isBlack: true },
  { note: "D", key: "s", isBlack: false },
  { note: "D#", key: "e", isBlack: true },
  { note: "E", key: "d", isBlack: false },
  { note: "F", key: "f", isBlack: false },
  { note: "F#", key: "t", isBlack: true },
  { note: "G", key: "g", isBlack: false },
  { note: "G#", key: "y", isBlack: true },
  { note: "A", key: "h", isBlack: false },
  { note: "A#", key: "u", isBlack: true },
  { note: "B", key: "j", isBlack: false },
  { note: "C2", key: "k", isBlack: false },
  { note: "C#2", key: "o", isBlack: true },
  { note: "D2", key: "l", isBlack: false },
  { note: "D#2", key: "p", isBlack: true },
  { note: "E2", key: ";", isBlack: false },
  { note: "F2", key: "'", isBlack: false },
];

const KEY_TO_NOTE: Record<string, string> = {};
KEYS.forEach((k) => {
  KEY_TO_NOTE[k.key] = k.note;
});

interface KeyboardProps {
  onNoteOn: (note: string) => void;
  onNoteOff: (note: string) => void;
  octave: number;
  onOctaveChange: (octave: number) => void;
  activeNotes: Set<string>;
  isActive: boolean;
}

export function Keyboard({
  onNoteOn,
  onNoteOff,
  octave,
  onOctaveChange,
  activeNotes,
  isActive,
}: KeyboardProps) {
  const pressedKeysRef = useRef<Set<string>>(new Set());

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (!isActive) return;

      const key = e.key.toLowerCase();

      // Prevent browser shortcuts
      if (KEY_TO_NOTE[key] || key === "z" || key === "x") {
        e.preventDefault();
        e.stopPropagation();
      }

      // Octave control
      if (key === "z") {
        onOctaveChange(Math.max(1, octave - 1));
        return;
      }
      if (key === "x") {
        onOctaveChange(Math.min(7, octave + 1));
        return;
      }

      if (KEY_TO_NOTE[key] && !pressedKeysRef.current.has(key)) {
        pressedKeysRef.current.add(key);
        onNoteOn(KEY_TO_NOTE[key]);
      }
    },
    [isActive, octave, onOctaveChange, onNoteOn]
  );

  const handleKeyUp = useCallback(
    (e: KeyboardEvent) => {
      const key = e.key.toLowerCase();
      if (KEY_TO_NOTE[key] && pressedKeysRef.current.has(key)) {
        pressedKeysRef.current.delete(key);
        onNoteOff(KEY_TO_NOTE[key]);
      }
    },
    [onNoteOff]
  );

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown);
    document.addEventListener("keyup", handleKeyUp);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("keyup", handleKeyUp);
    };
  }, [handleKeyDown, handleKeyUp]);

  const handleMouseDown = (note: string) => (e: React.MouseEvent) => {
    e.preventDefault();
    onNoteOn(note);
  };

  const handleMouseUp = (note: string) => () => {
    onNoteOff(note);
  };

  const handleMouseLeave = (note: string) => () => {
    if (activeNotes.has(note)) {
      onNoteOff(note);
    }
  };

  const handleTouchStart = (note: string) => (e: React.TouchEvent) => {
    e.preventDefault();
    onNoteOn(note);
  };

  const handleTouchEnd = (note: string) => () => {
    onNoteOff(note);
  };

  return (
    <div className="overflow-x-auto -mx-6 px-6 md:mx-0 md:px-0 pb-2">
      <div className="flex justify-start md:justify-center gap-px p-2 md:p-4 bg-[#0a0a0a] rounded-lg w-fit md:w-auto mx-auto">
        {KEYS.map((k) => {
          const isActive = activeNotes.has(k.note);

          if (k.isBlack) {
            return (
              <div
                key={k.note}
                onMouseDown={handleMouseDown(k.note)}
                onMouseUp={handleMouseUp(k.note)}
                onMouseLeave={handleMouseLeave(k.note)}
                onTouchStart={handleTouchStart(k.note)}
                onTouchEnd={handleTouchEnd(k.note)}
                className="relative w-4 md:w-6 h-[50px] md:h-[75px] rounded-b z-10 -mx-2 md:-mx-3 cursor-pointer select-none touch-none transition-all"
                style={{
                  background: isActive
                    ? "linear-gradient(180deg, #00cc6a, #009950)"
                    : "linear-gradient(180deg, #333, #111)",
                  boxShadow: isActive ? "0 0 12px rgba(0,255,136,0.5)" : "none",
                  transform: isActive ? "translateY(1px)" : "none",
                }}
              >
                <span className="absolute bottom-1 left-1/2 -translate-x-1/2 text-[6px] md:text-[8px] text-[#555] pointer-events-none uppercase">
                  {k.key}
                </span>
              </div>
            );
          }

          return (
            <div
              key={k.note}
              onMouseDown={handleMouseDown(k.note)}
              onMouseUp={handleMouseUp(k.note)}
              onMouseLeave={handleMouseLeave(k.note)}
              onTouchStart={handleTouchStart(k.note)}
              onTouchEnd={handleTouchEnd(k.note)}
              className="relative w-6 md:w-9 h-[80px] md:h-[120px] rounded-b z-0 cursor-pointer select-none touch-none transition-all"
              style={{
                background: isActive
                  ? "linear-gradient(180deg, #00ff88, #00cc6a)"
                  : "linear-gradient(180deg, #e8e8e8, #fff)",
                border: isActive ? "1px solid #00ff88" : "1px solid #ccc",
                boxShadow: isActive ? "0 0 15px rgba(0,255,136,0.6)" : "none",
                transform: isActive ? "translateY(2px)" : "none",
              }}
            >
              <span className="absolute bottom-1.5 left-1/2 -translate-x-1/2 text-[7px] md:text-[9px] text-[#999] pointer-events-none uppercase">
                {k.key}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
